import { defineStore } from 'pinia'

export type SignalSource = 'stated' | 'inferred'

// `value` is the canonical we matched — what readiness, the request and any
// backend matching key off. `display` is the buyer's own wording when it differs:
// they said "husband", we resolved "spouse". Rendering reads `display ?? value`,
// so a signal without one still shows something sensible.
//
// One field couldn't do both jobs. Sending surface text to the endpoint gives it a
// vocabulary it can't match on; showing the canonical back to the buyer puts words
// in their mouth mid-sentence.
export type Signal<T> = {
  value: T | null
  display: string | null
  source: SignalSource | null
}

export type BudgetRange = {
  min: number | null
  max: number | null
}

export type ConciergeSignals = {
  location: Signal<string>
  relationship: Signal<string>
  interests: { value: string[], display: string | null, source: SignalSource | null }
  occasion: Signal<string>
  budget: Signal<BudgetRange>
}

export type SignalName = keyof ConciergeSignals

export const SIGNAL_NAMES: SignalName[] = ['location', 'relationship', 'interests', 'occasion', 'budget']

// An options bag rather than trailing positional arguments: `source` and `display`
// are both nullable strings, so positionally they'd be easy to transpose, and a
// caller that wants a display but not a source shouldn't have to restate the
// default.
export type SignalOptions = {
  source?: SignalSource
  display?: string | null
}

// The buyer's own wording per signal, keyed the same way the parsed values are, so
// a parse can hand back both halves without pairing them up field by field.
export type SignalSurfaces = Partial<Record<SignalName, string>>

export type SignalValues = {
  location: string
  relationship: string
  interests: string[]
  occasion: string
  budget: BudgetRange
}

export type TriageOutcome = 'concierge' | 'fast_path' | 'support'

export function emptySignals (): ConciergeSignals {
  return {
    location: { value: null, display: null, source: null },
    relationship: { value: null, display: null, source: null },
    interests: { value: [], display: null, source: null },
    occasion: { value: null, display: null, source: null },
    budget: { value: null, display: null, source: null },
  }
}

// Deliberately keyed on `value`, not `display`: a surface form we couldn't resolve
// to anything isn't a usable signal, and letting it count would let readiness fire
// on text the matcher can't search with.
export function signalFilled (signal: ConciergeSignals[SignalName]): boolean {
  return Array.isArray(signal.value) ? signal.value.length > 0 : signal.value !== null
}

export const useConciergeState = defineStore('buyersConcierge', () => {
  const raw = ref('')
  const signals = ref<ConciergeSignals>(emptySignals())
  const triageOutcome = ref<TriageOutcome | null>(null)

  const filledSignalNames = computed(() =>
    SIGNAL_NAMES.filter(name => signalFilled(signals.value[name])))

  // `value: null` with a display is a real state, not an error: the buyer wrote
  // something no list resolves, so their words render but nothing counts toward
  // readiness or reaches the request (see the signalFilled comment above). Only the
  // scalar signals accept null — `interests` is always an array (empty via
  // clearSignal), so the type keeps null out of it.
  function setSignal<Name extends SignalName> (
    name: Name,
    value: Name extends 'interests' ? SignalValues[Name] : SignalValues[Name] | null,
    { source = 'stated', display = null }: SignalOptions = {},
  ) {
    signals.value[name] = { value, display, source } as ConciergeSignals[Name]
  }

  function clearSignal (name: SignalName) {
    signals.value[name] = emptySignals()[name] as never
  }

  // Merge rule: a stated value always wins; an inferred value never
  // overwrites one the buyer stated.
  function applySignals (parsed: Partial<SignalValues>, source: SignalSource, surfaces: SignalSurfaces = {}) {
    for (const name of SIGNAL_NAMES) {
      const value = parsed[name]
      if (value === undefined) continue
      if (source === 'inferred' && signals.value[name].source === 'stated') continue

      setSignal(name, value as SignalValues[typeof name], { source, display: surfaces[name] ?? null })
    }
  }

  // Live re-parse owns the whole signal set: each keystroke re-reads the full
  // text, so signals the buyer deleted must clear too. Wholesale replace, not a
  // merge. (Manually-corrected signals surviving a re-parse is a later ticket.)
  function replaceFromParse (parsed: Partial<SignalValues>, surfaces: SignalSurfaces = {}) {
    signals.value = emptySignals()
    applySignals(parsed, 'stated', surfaces)
  }

  function setTriageOutcome (outcome: TriageOutcome) {
    triageOutcome.value = outcome
  }

  function reset () {
    raw.value = ''
    signals.value = emptySignals()
    triageOutcome.value = null
  }

  return {
    raw,
    signals,
    triageOutcome,
    filledSignalNames,
    setSignal,
    clearSignal,
    applySignals,
    replaceFromParse,
    setTriageOutcome,
    reset,
  }
}, {
  // sessionStorage, not localStorage or the Rails session: the homepage is
  // server-rendered, so every result click is a full page load and the island
  // re-hydrates from scratch — the buyer's raw text + parsed signals must survive
  // homepage → gift page → checkout within the tab (the checkout write-through
  // reads them there), but shouldn't outlive the tab. Only raw/signals/triage are
  // picked — results aren't in the store; the results surface refetches per page.
  persist: {
    key: 'giftly.buyersConcierge',
    // typeof guard, not import.meta.env.SSR: the plugin isn't installed on
    // the SSR server (see @consumer/services/store), but this options object
    // still evaluates there — and Vitest sets SSR=true while jsdom has a
    // perfectly good sessionStorage.
    storage: (typeof sessionStorage === 'undefined' ? undefined : sessionStorage) as Storage,
    pick: ['raw', 'signals', 'triageOutcome'],
  },
})
